<template>
  <div :class="props.class" :style="props.style" v-bind="props.attrs">
    <slot />  
    <div ref="itemsRef" :class="`promotion-code-box ${prepare.dataStyle}`">
      <div class="promotion-items">
        <keep-alive>
          <component 
            v-for="item in state.items" 
            :key="item.id" 
            class="promotion-item" 
            :data-id="item.id"
            :is="item"
          ></component>
        </keep-alive>

        <V3InfiniteLoading 
          :direction="prepare.theme == 'theme-5' ? '' : 'right'" 
          :arrived-state="arrivedState" 
          :firstload="false"
          @infinite="loadData"
          >
          <template #complete>
            <span></span>
          </template>
          <template #spinner>
            <span></span>
          </template>
        </V3InfiniteLoading>
      </div>
    </div>
    <div class="text-danger h1">{{ state.helpText }}</div>
  </div>
</template>

<script setup>
import { reactive, computed, ref, toRaw, onMounted, h } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n"
import { cloneDeep } from "lodash"
import { useScroll } from '@vueuse/core'
import V3InfiniteLoading from "v3-infinite-loading";
import {
  _api_getPromotions,
} from "../../../api/index.js";
import {
  notificationErrors,
  currencyFormat,
  assignCartCount,
  clearCartCount,
  dataTranslations,
  breakpoint,
  lookupAliasTranslations,
  dashToHump,
} from "../../../utils";
import { renderHtml, getAttributesFromNode } from "../../../plugins/html-to-vue/index.js";
import { ElButton, ElImage } from 'element-plus'

const props = defineProps({
  class: String,
  style: String,
  attrs: Object,
})

const { t, locale } = useI18n()
const store = useStore();
const { x, y, isScrolling, arrivedState, directions } = useScroll(itemsRef, { behavior: 'smooth' })

const prepare = computed(() => {
  let dataSets = {
    page: 1,
  }
  // console.debug("prepare", props)
  for (let value in props.attrs) {
    if (["style", "class"].includes(value)) {
      continue
    }
    const textArr = dashToHump(value);
    dataSets[textArr] = aibTrackers.isNumber(props.attrs[value]) ? Number(props.attrs[value]) : props.attrs[value]
    if (["dataSourceManualIds"].includes(textArr)) {
      if (dataSets[textArr] == 0) {
        dataSets[textArr] = null
      }
    }
  }
  if (dataSets["dataSource"] == "manual") {
    if (!dataSets["dataSourceManualIds"]) {
      dataSets.page = 0
    } else {
      dataSets["dataSourceManualIds"] = dataSets["dataSourceManualIds"].split(',');
    }
  }
  dataSets["params"] = {
    status: "active",
    fields: "banner_media,urls,tags,conditions,store_products",
    is_hidden: 0,
    limit: 12,
    type: dataSets.dataType,
    store_id: dataSets.dataStoreId,
    currency: dataSets.dataCurrency,
    lang: dataSets.dataLang,
    ids: dataSets.dataSourceManualIds,
    page: dataSets.page,
  }
  if (elemObj.value) {
    const itemTemplate = elemObj.value.find('.promotion-item').first();
    dataSets["template"] = () => itemTemplate.clone(true)
  }
  return dataSets
})

const state = reactive({
  apiData: null,
  items: [],
  filterParams: null,
  helpText: null,
})
const itemsRef = ref()
const elemObj = ref(null)

const parseTemplate = (rawHtml, item) => {
  const conf = {
    config: {
      container: {
        type: 'div'
      },
      extraComponentsMap: {
        btnLink: {
          conditions(node) {
            return (
              node.type === 'tag' && // is a tag
              node.name === 'div' && // is an anchor
              node.attrs?.class?.match(/promotion-btn\s?/) // has btn class
            )
          },
          renderer(node, children, context) {
			      const options = getAttributesFromNode(node)
            return h(
              ElButton,
              {
                class: options.class,
                style: options.style,
                title: context.title,
                onClick: (e) => {
                  e.stopPropagation()
                  // console.debug("emit_event", context?.id)
                  if (context?.id) {
                    store.dispatch("lite/setFullPageLoading", true);
                    window.location.assign(`/promotions/${context?.id}`);
                    store.dispatch("lite/setFullPageLoading", false);
                  }
                }
              },
              () => [...children] // parsed children, in our case it will probably be just a text child
            )
          }
        },
      },
      textTransformer: text => text.trim()
    },

    rawHtml: rawHtml,
  }
  return renderHtml(conf.rawHtml, conf.config, item)
}
const setItemTemplate = (item) => {
  let template = prepare.value.template()

  template.attr("data-id", item?.id)

  let infoText = "";
  if (item.condition_scope == 'subtotal') {
      infoText = "訂單金額達標";
  } else if (item.condition_scope == 'products') {
      infoText = "指定商品參與";
  } else if (item.condition_scope == 'categories') {
      infoText = "指定分類參與";
  } else if (item.condition_scope == 'promotions') {
      infoText = "指定活動參與";
  } else if (item.condition_scope == 'promotion_groups') {
      infoText = "指定活動組參與";
  }
  let priceText = "100";
  let currency = prepare.value?.dataCurrencySymbol;
  if (item.discount_type == "non") {
    priceText = "不指定";
  } else {
    if (item.conditions.length) {
      for (let condition of item?.conditions) {
        
        if (item.discount_type == "amount") {
          if (!condition.discount_amount) {
            continue;
          }
          priceText = condition.discount_amount;
          currency = item.currency_symbol;
        } else if (item.discount_type == "percentage") {
          if (!condition.discount_percentage) {
            continue;
          }
          priceText = condition.discount_percentage+'%';
        } else if (item.discount_type == "gift") {
          if (!condition.discountable_quantity) {
            continue;
          }
          priceText = '送贈品';
        } 
        break;
      }
    }
  }
  
  template.find(".promotion-desc").text(item?.title)
  template.find(".promotion-info").text(infoText)
  template.find(".promotion-price span").text(priceText)
  template.find(".promotion-price small").text(currency)

  // console.debug("item", item)
  return parseTemplate(template.html(), item)
}
// 檢查是否還有商品需要載入
const canLoadMore = computed(() => {
  if (!state.apiData?.paging && state.filterParams?.page > 0) {
    return true
  }
  const dataLen = state.items.length || 0
  if (state.apiData?.paging?.total_count > dataLen && state.filterParams?.page > 0) {
    if (setting.quantity > 0) {
      if (setting.quantity > dataLen) {
        return true;
      }
      return false;
    } else {
      // 不限制數量
      return true;
    }
  }
  return false;
})

const getDatas = async () => {
  if (!state.filterParams?.page) {
    state.helpText = '尚未設定活動來源!';
    return;
  }

  store.dispatch("lite/setFullPageLoading", true);
  try {
    state.apiData = await _api_getPromotions(state.filterParams)
    if (state.apiData.data.length) {
      if (!prepare.value.template()) {
        return
      }
      state.apiData.data.forEach((item) => {
        let template = setItemTemplate(item)
        state.items.push(template)
      })
      state.filterParams.page++

      // console.debug("items", state.items)
      const eventID = state.apiData?.headers?.["x-request-id"] ? state.apiData?.headers?.["x-request-id"] : null
      initTracker(state.apiData.data, eventID)
      elemObj.value.find('.promotion-code-box').first().remove()
    }
  } catch (error) {
    notificationErrors(error);
  } finally {
    store.dispatch("lite/setFullPageLoading", false);
  }
}
const loadData = async ($state) => {
  if (!$state) {
    return
  }
  try {
    // console.debug("arrivedState", arrivedState)
    if (canLoadMore.value) {
      await getDatas()
    }
    if (!canLoadMore.value) {
      console.debug("complete")
      $state.complete()
    } else {
      console.debug("loaded")
      $state.loaded()
    }
    
  } catch (err) {
    $state.error()
    notificationErrors(err)
  } finally {
    // window.fullPageLoading("hide")
  }
}

const initTracker = (items = [], eventID = null) => {
  console.debug("initTracker-折扣碼", eventID)

  aibTrackers.viewContentAddonProduct(items, eventID)
  gtmTrackers.viewContentAddonProduct(items, eventID)
  fbqTrackers.viewContentAddonProduct(items, eventID)
  ttqTrackers.viewContentAddonProduct(items, eventID)
  gtagTrackers.viewContentAddonProduct(items, eventID)
}

onMounted(() => {
  if (typeof prepare.value?.id !== 'undefined') {
    elemObj.value = $("#"+prepare.value.id);
    state.filterParams = cloneDeep(prepare.value.params)
    // 移除範本, 僅保留一個當作渲染用
    const sample = elemObj.value.find('.promotion-code-box').first()
    if (sample) {
      sample.find('.promotion-item').each((index, item) => {
        if (index > 0) {
          $(item).remove()
        }
      })
    }
    getDatas()
  }
  // console.debug("prepare", prepare.value)
})
</script>
